/* eslint-disable restrict-imports/restrict-imports */
import { Box } from '@material-ui/core';
import React from 'react';
import {
  ItemType,
  ILabeledItem,
  IPlaceholderItem,
  IUnlabeledItem,
  ILabelOrPlaceholderItems,
  ITiedItem,
  ITiedItems,
} from './types';
import { useDrop, useDrag } from 'react-dnd';
import { makeStyles } from 'design-system';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DEFAULT_OPTION_LABEL } from './UnlabeledItems';

interface LabeledOrPlaceholderTiedItemProp {
  tiedItem: ITiedItem;
  onDropToLabeledOrPlaceholderItem: (
    dragItem: IUnlabeledItem | ILabeledItem,
    dropItem: ILabeledItem | IPlaceholderItem,
  ) => void;
  disabled?: boolean;
}

const LabeledOrPlaceholderTiedItem: React.FC<LabeledOrPlaceholderTiedItemProp> = ({
  tiedItem,
  onDropToLabeledOrPlaceholderItem,
  disabled,
}) => {
  const classes = useStyles();
  const label = tiedItem.itemLabel || DEFAULT_OPTION_LABEL;

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: [ItemType.UnlabeledItem, ItemType.LabeledItem],
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop: (item: IUnlabeledItem | ILabeledItem) => {
      onDropToLabeledOrPlaceholderItem(item, tiedItem);
    },
  }));

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: tiedItem.dndType,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
      item: tiedItem,
      canDrag: !disabled,
    }),
    [disabled],
  );

  const dragAndDrop = (el: any) => {
    drag(el);
    drop(el);
  };

  return (
    <>
      {tiedItem.dndType === ItemType.LabeledItem ? (
        <div
          ref={dragAndDrop}
          className={cx(classes.labeledItemContainer, {
            [classes.isSelfDragging]: isDragging, // this item being dragged
            [classes.isDragging]: canDrop, // anything being dragged
            [classes.isOver]: isOver,
          })}
        >
          <span>{`${label} ${Number(tiedItem.id) + 1}`}</span>
          <span className={classes.gripIcon}>
            <FontAwesomeIcon icon="grip-vertical" />
          </span>
        </div>
      ) : (
        <div
          ref={drop}
          className={cx(classes.placeholderItemContainer, {
            [classes.isOver]: isOver,
            [classes.isDragging]: canDrop,
          })}
        ></div>
      )}
    </>
  );
};

interface LabeledOrPlaceholderTiedItemsProps {
  labeledOrPlaceholderItem: ITiedItems;
  itemIndex: number;
  onDropToLabeledOrPlaceholderItem: (
    dragItem: IUnlabeledItem | ILabeledItem,
    dropItem: ILabeledItem | IPlaceholderItem,
  ) => void;
  onDeleteTiedItems: (tiedItems: ITiedItems) => void;
  disabled?: boolean;
}

const LabeledOrPlaceholderTiedItems: React.FC<LabeledOrPlaceholderTiedItemsProps> = ({
  labeledOrPlaceholderItem: tiedItems,
  itemIndex,
  onDropToLabeledOrPlaceholderItem,
  onDeleteTiedItems,
  disabled,
}) => {
  const classes = useStyles();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <div className={classes.itemIndex}>{itemIndex + 1}</div>
      <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
        {tiedItems.map(tiedItem => {
          return (
            <LabeledOrPlaceholderTiedItem
              key={tiedItem.id}
              tiedItem={tiedItem}
              onDropToLabeledOrPlaceholderItem={onDropToLabeledOrPlaceholderItem}
              disabled={disabled}
            />
          );
        })}
      </Box>
      <div
        className={classes.deleteIcon}
        onClick={() => {
          if (!disabled) {
            onDeleteTiedItems(tiedItems);
          }
        }}
      >
        <FontAwesomeIcon icon="times" />
      </div>
    </Box>
  );
};

interface LabeledOrPlaceholderItemsProps {
  labeledOrPlaceholderItems: ILabelOrPlaceholderItems;
  onDropToLabeledOrPlaceholderItem: (
    dragItem: IUnlabeledItem | ILabeledItem,
    dropItem: ILabeledItem | IPlaceholderItem,
  ) => void;
  onDeleteTiedItems: (tiedItems: ITiedItems) => void;
  disabled?: boolean;
}

const LabeledOrPlaceholderItems: React.FC<LabeledOrPlaceholderItemsProps> = ({
  labeledOrPlaceholderItems,
  onDropToLabeledOrPlaceholderItem,
  onDeleteTiedItems,
  disabled,
}) => {
  const classes = useStyles();
  return (
    <div className={classes.itemsContainer}>
      {labeledOrPlaceholderItems.map((tiedItems, index) => {
        return (
          <LabeledOrPlaceholderTiedItems
            key={tiedItems.map(item => item.id).join(',')}
            labeledOrPlaceholderItem={tiedItems}
            itemIndex={index}
            onDropToLabeledOrPlaceholderItem={onDropToLabeledOrPlaceholderItem}
            onDeleteTiedItems={onDeleteTiedItems}
            disabled={disabled}
          />
        );
      })}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  itemsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemIndex: {
    color: theme.colors.gray50,
  },
  labeledItemContainer: {
    background: theme.colors.white,
    borderRadius: '10px',
    height: '40px',
    border: `1px solid ${theme.colors.gray20}`,
    overflow: 'hidden',
    margin: '4px',
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    padding: `0px ${theme.spacing(2)}px`,
    transform: 'translate(0, 0)',
    position: 'relative',
    cursor: 'pointer',
    width: '80px',
  },
  placeholderItemContainer: {
    background: theme.colors.gray01,
    borderRadius: '10px',
    height: '40px',
    border: `1px solid ${theme.colors.coolGray30}`,
    overflow: 'hidden',
    margin: '4px',
    flexGrow: 1,
    transform: 'translate(0, 0)',
    width: '80px',
  },
  isOver: {
    background: theme.colors.pink1,
  },
  isDragging: {
    border: `1px dashed ${theme.colors.gray20}`,
  },
  isSelfDragging: {
    opacity: 0.5,
  },
  deleteIcon: {
    cursor: 'pointer',
  },
  gripIcon: {
    color: theme.colors.gray50,
    position: 'absolute',
    right: theme.spacing(2),
  },
}));

export default LabeledOrPlaceholderItems;
